/**
 * Place action — the one thing the guest's screen is asking them to do.
 *
 * WHY THIS EXISTS. `resolvePlaceState` already works out the guest's situation
 * with the right precedence, but that answer was only ever rendered as a 34px
 * text strip under the header. Meanwhile the *actionable* version of the same
 * facts was scattered across three separate pieces of bottom furniture that
 * could all be on screen at once:
 *
 *   · PlaceRail          — four tabs, fixed, one of which was the page you were on
 *   · MiniCartBar        — the cart, rendered by the ordering island, on top of the rail
 *   · PublicMenuActionBar — a floating Cart/Discover/Orders pill, also on top of the rail
 *
 * Testers read that stack as "I don't know how to navigate": a single restaurant
 * menu is ONE screen, and putting app-shell tab furniture on it implies
 * destinations that do not exist, while the thing they actually needed — "you
 * have 3 items, send them" — was the smallest element of the three.
 *
 * So the bottom of the screen becomes ONE bar with ONE job, and this function
 * decides what that job is. The bar is absent when there is no job: a guest
 * reading a menu with an empty cart gets the whole screen for food.
 *
 * PURE. Same reasoning as place-state.ts: no React, no clocks, no fetch, so the
 * server render and the client render cannot disagree about what the bar says.
 *
 * @see components/place/PlaceActionBar.tsx — the sole renderer
 * @see lib/customer/place-state.ts — the situation this reads
 */

import type { PlaceState } from './place-state';

/** One fulfilment choice, in the guest's words. */
export type PlaceActionOption = {
  key: string;
  label: string;
  /** One short clause: what picking this actually means for them. */
  hint?: string | null;
};

export type PlaceActionModel =
  /** Nothing to ask. The bar does not render at all. */
  | { kind: 'idle' }
  /**
   * The guest has chosen food but not yet said how they want it. Asked HERE,
   * at the moment it first has a consequence — never on arrival, where it is a
   * question about logistics put to someone who has not seen the food yet.
   */
  | { kind: 'fulfilment'; question: string; options: PlaceActionOption[] }
  /** Items chosen, nothing sent. */
  | { kind: 'cart'; lead: string; detail: string | null; cta: string }
  /**
   * An order is with the kitchen. The bar becomes the tracker.
   *
   * Carries an ACTION, not an href: there is no `/m/<slug>/order` page. The
   * guest's orders sheet opens over the menu instead, which is both real and
   * the behaviour the shell wants anyway.
   */
  | { kind: 'live'; lead: string; detail: string | null; cta: string; action: string; href?: string; urgent: boolean }
  /** Money outstanding. The only state that gets the attention tone. */
  | { kind: 'pay'; lead: string; detail: string | null; cta: string; action: string; href?: string }
  /**
   * A place whose whole point is a booking rather than a cart (a caterer, an
   * events venue). Its conversion action is persistent because there is no cart
   * to fill and therefore nothing else the bar could ever be doing.
   */
  | { kind: 'invite'; lead: string | null; cta: string; href: string };

export type PlaceActionInput = {
  state: PlaceState;
  /** Formatted by the caller — this module never touches money. */
  cartTotal?: string | null;
  cartCount?: number;
  /**
   * Set while the guest has items but has not answered "how do you want this".
   * Null once answered, or when the branch offers exactly one way (in which case
   * the caller has already applied it — a question with one answer is not a
   * question).
   */
  fulfilment?: { question: string; options: PlaceActionOption[] } | null;
  /** The booking-led place's standing call to action. */
  invite?: { label: string; href: string; lead?: string | null } | null;
  /** "order" / "booking" / "quote", from the business-type vocabulary. */
  orderNoun?: string;
};

/**
 * Resolve the bar.
 *
 * PRECEDENCE DELIBERATELY DIFFERS FROM place-state's, in one place. That
 * resolver ranks a live order above the cart, and is right to: it answers "what
 * is the most important true thing about this guest", and food already with the
 * kitchen outranks a half-built basket.
 *
 * The bar answers a different question — "what is this guest trying to DO" —
 * and there the cart wins. A dine-in guest who orders drinks and then starts
 * adding food has an order in flight AND a cart; ranking the order first would
 * replace their basket with a tracker and leave the second round unreachable.
 * The order is still visible (the orders sheet, and a push notification when it
 * is ready); a cart that cannot be opened is simply lost work.
 *
 * Money outstanding still outranks everything: it is the one state where the
 * guest is blocking and the merchant is exposed.
 *
 * An unanswered fulfilment question outranks the cart summary for the same
 * reason — a "Review order" button leading to a checkout blocked on a question
 * nobody asked is the dead end this redesign exists to remove.
 */
export function resolvePlaceAction(input: PlaceActionInput): PlaceActionModel {
  const { state } = input;
  const noun = input.orderNoun?.trim() || 'order';

  // 1. Money outstanding. Nothing else matters while the guest owes.
  if (state.stance === 'awaiting_payment') {
    return {
      kind: 'pay',
      lead: state.label,
      detail: input.cartTotal || state.detail || null,
      cta: 'Pay now',
      action: state.action || 'openOrders',
      // Only ever present when there is a REAL page to point at. Omitted
      // entirely otherwise, so "this model has an href" keeps meaning "there is
      // somewhere to go" — the guarantee the route-existence test below relies on.
      ...(state.href ? { href: state.href } : {}),
    };
  }

  // 2. Items chosen and not yet sent — the guest's own unfinished work.
  const cartCount = input.cartCount ?? 0;
  if (cartCount > 0) {
    if (input.fulfilment && input.fulfilment.options.length > 0) {
      return { kind: 'fulfilment', question: input.fulfilment.question, options: input.fulfilment.options };
    }
    return {
      kind: 'cart',
      lead: cartCount === 1 ? '1 item' : `${cartCount} items`,
      detail: input.cartTotal || null,
      cta: `Review ${noun}`,
    };
  }

  // 3. Nothing in the basket, but food is coming. The bar becomes the tracker.
  if (state.stance === 'live_order') {
    return {
      kind: 'live',
      lead: state.label,
      detail: state.detail ?? null,
      cta: 'Track',
      action: state.action || 'openOrders',
      // Only ever present when there is a REAL page to point at. Omitted
      // entirely otherwise, so "this model has an href" keeps meaning "there is
      // somewhere to go" — the guarantee the route-existence test below relies on.
      ...(state.href ? { href: state.href } : {}),
      urgent: state.tone === 'attention',
    };
  }

  // 4. A booking-led place has a standing invitation rather than a cart.
  if (input.invite) {
    return {
      kind: 'invite',
      lead: input.invite.lead ?? null,
      cta: input.invite.label,
      href: input.invite.href,
    };
  }

  // 5. Reading the menu with an empty cart. Give the screen back to the food.
  return { kind: 'idle' };
}

/** True when the bar will render something, so the shell knows whether to
 *  reserve bottom clearance. Kept here so the shell and the bar cannot drift. */
export function placeActionOccupiesSpace(model: PlaceActionModel): boolean {
  return model.kind !== 'idle';
}
